import logger from '../../log';
import { ConstructedEvents } from '../../common/Reporter/constants';
import { dispatchPlayerEvent } from '../../events/dispatch';
import { HLSEventCallback } from '@kollective-frontend/ksdk-api';
import {
  BasePlayerEvent,
  TechApiState,
  TrackInfo,
  TrackType
} from '../../reporter/constants';

const log = logger.getLogger(__filename);

const {
  audiochanged: { code: audiochanged },
  captionchanged: { code: captionchanged },
  playbackbitratechanged: { code: bitratechanged }
} = ConstructedEvents;

export interface HLSjsPlayer {
  on: (name: string, handler: HLSEventCallback) => void;
  off: (name: string, handler?: HLSEventCallback) => void;
  once: (name: string, handler: HLSEventCallback) => void;
  levels?: HLSLevelInformation[];
  currentLevel?: number;
  audioTrack?: number;
  audioTracks?: HLSTrack[];
  subtitleTrack?: number;
  subtitleTracks?: HLSTrack[];
}

export interface HlsEventState extends TechApiState {
  levels: HlsLevelSwitchingData[];
  audioTrack?: number;
  audioTracks?: HLSTrack[];
  subtitleTrack?: number;
  subtitleTracks?: HLSTrack[];
}

interface TrackListsData {
  backup?: TrackInfo;
  p1: { track?: number; tracks?: HLSTrack[] };
  p2: { track?: number; tracks?: HLSTrack[] };
}

//#region Hls Events
export interface HlsLevelSwitchingData {
  bitrate: number;
}

export interface HLSLevelInformation {
  bitrate: number;
  height: number;
  width: number;
}

export interface HLSTrack {
  lang: string;
  name: string;
}

export interface HlsAudioTrackSwitched {
  id: number;
  lang: string /* en, es, de, etc. */;
  name: string;
}

export interface HlsAudioTracksUpdated {
  audioTracks: HLSTrack[];
}

export interface HlsLevelLoadedEvent {
  details: HlsLevelSwitchingData;
  level: number;
}

export interface HlsManifestLoadedEvent {
  levels: HlsLevelSwitchingData[];
  firstLevel: number;
}

export interface HlsSubtitleTrackLoaded {
  id: number;
  track: TrackInfo;
}

export interface HlsSubtitleTrackSwitch {
  id: number;
}

export interface HlsSubtitleTracksUpdated {
  subtitleTracks: HLSTrack[];
}

// Amalgam of all available events, individual events DO NOT have all the
// fields.
export interface HlsEventData {
  audioTrack: number;
  audioTracks: HLSTrack[];
  bitrate: number;
  details: HlsLevelSwitchingData;
  firstLevel: number;
  height: number;
  id: number;
  lang: string;
  level: number;
  levels: HlsLevelSwitchingData[];
  name: string;
  subtitleTrack: number;
  subtitleTracks: HLSTrack[];
  track: TrackInfo;
  width: number;
}
//#endregion Hls Events

export type HlsEventHandler = (n: string, evt: HlsEventData) => void;

/**
 * Get the information for the active track.
 *
 * @param type - type of the track we're looking for (only used for logging)
 * @param backup - a fallback TrackInfo object
 * @param track - the index of the currently active track
 * @param tracks - the list of available tracks
 * @returns
 */
export function getTrackInfoFromTracks(
  type: string,
  backup?: TrackInfo,
  track?: number,
  tracks?: HLSTrack[]
): TrackInfo | undefined {
  if (typeof track !== 'undefined' && typeof tracks !== 'undefined') {
    if (tracks.length === 0) {
      return;
    }
    if (track === -1 || track > tracks.length) {
      log.trace(`No ${type} tracks active`);
      return { lang: '', name: '' };
    }
    const { lang, name } = tracks[track] ?? { lang: '', name: '' };
    log.trace(`${type} tracks:`, { lang, name });
    return { lang, name };
  }
  log.trace(`track info ${type} falling back to default:`, backup, {
    track,
    tracks
  });
  return backup;
}

/**
 * getTracksFromTrackType.
 *
 * @param type -
 * @param data -
 * @param hls -
 * @returns
 */
function getTracksFromTrackType(
  type: 'audio' | 'caption',
  data: HlsEventState,
  hls: HLSjsPlayer
): TrackListsData {
  if (type === 'audio') {
    return {
      backup: data.at,
      p1: { track: hls.audioTrack, tracks: hls.audioTracks },
      p2: { track: data.audioTrack, tracks: data.audioTracks }
    };
  }
  return {
    backup: data.ct,
    p1: { track: hls.subtitleTrack, tracks: hls.subtitleTracks },
    p2: { track: data.subtitleTrack, tracks: data.subtitleTracks }
  };
}

export function getTrackInfo(
  type: 'audio' | 'caption',
  data: HlsEventState,
  hls: HLSjsPlayer
): () => TrackInfo | undefined {
  return (): TrackInfo | undefined => {
    const { backup, p1, p2 } = getTracksFromTrackType(type, data, hls);
    if (typeof p1.track !== 'undefined' && typeof p1.tracks !== 'undefined') {
      return getTrackInfoFromTracks(type, backup, p1.track, p1.tracks);
    } else if (typeof p2.track !== 'undefined' && p2.tracks) {
      return getTrackInfoFromTracks(type, backup, p2.track, p2.tracks);
    }
    return backup;
  };
}

/**
 * maybeDispatchTrackEvent.
 *
 * @param eventName - name of the kollective event
 * @param type -
 * @param handler -
 */
export function maybeDispatchTrackEvent(
  eventName: string,
  type: TrackType,
  handler: () => TrackInfo | undefined
): void {
  const trackInfo = handler();
  if (!trackInfo) {
    log.trace(eventName, 'no track info found');
    return;
  }
  const e = type == 'at' ? audiochanged : captionchanged;
  const event: BasePlayerEvent = { e, [type]: trackInfo };
  log.trace(eventName, 'dispatching:', event);
  dispatchPlayerEvent(event);
}

export function getCurrentBitrate(
  hls: HLSjsPlayer,
  data: HlsEventState
): number | undefined {
  try {
    const current = hls?.currentLevel ?? -1;
    const levels = hls.levels ?? data.levels;
    const length = levels.length ?? -1;
    if (current > -1 && current < length) {
      const level = levels[current];
      log.trace('last bitrate:', level, data.lastBitrate);
      data.lastBitrate = level?.bitrate ?? data.lastBitrate;
    }
  } catch (e) {
    log.info('Failure getting last bitrate:', hls, e);
  }
  return data.lastBitrate;
}

export interface GenerateHandlerArgs {
  hls: HLSjsPlayer;
  audioChangeHandler: () => TrackInfo | undefined;
  captionChangeHandler: () => TrackInfo | undefined;
  data: HlsEventState;
}
type TrackInfoRetriever = () => TrackInfo | undefined;

export interface HlsJsEventHandlers {
  handlers: Record<string, HLSEventCallback>;
  audioChangeHandler: TrackInfoRetriever;
  bitrateChangeHandler: () => number | undefined;
  captionChangeHandler: TrackInfoRetriever;
  data: HlsEventState;
}

function generateHlsJsEventHandlers(hls: HLSjsPlayer): HlsJsEventHandlers {
  const data: HlsEventState = { levels: [] };
  const captionChangeHandler = getTrackInfo('caption', data, hls);
  const bitrateChangeHandler = () => getCurrentBitrate(hls, data);
  const audioChangeHandler = getTrackInfo('audio', data, hls);
  const handlers = {
    // Sets up for future audio changes
    hlsAudioTracksUpdated: (_: string, evt: HlsAudioTracksUpdated) => {
      data.audioTracks = evt.audioTracks ?? data.audioTracks;
    },
    hlsAudioTrackSwitched: (name: string, evt: HlsAudioTrackSwitched) => {
      data.at = { lang: evt?.lang, name: evt?.name };
      maybeDispatchTrackEvent(name, 'at', audioChangeHandler);
    },
    hlsLevelSwitching: (eventName: string, evt: HlsLevelSwitchingData) => {
      log.trace(eventName, evt);
      if (evt && data.lastBitrate !== evt.bitrate) {
        data.lastBitrate = evt.bitrate;
        dispatchPlayerEvent({ e: bitratechanged, b: evt.bitrate });
      }
    },
    hlsSubtitleTrackLoaded: (name: string, evt: HlsSubtitleTrackLoaded) => {
      data.ct = { lang: evt?.track?.lang, name: evt?.track?.name };
      maybeDispatchTrackEvent(name, 'ct', captionChangeHandler);
    },
    hlsSubtitleTrackSwitch: (name: string, evt: HlsSubtitleTrackSwitch) => {
      data.subtitleTrack = evt?.id;
      maybeDispatchTrackEvent(name, 'ct', captionChangeHandler);
    },
    // Sets up for future caption changes
    hlsSubtitleTracksUpdated: (_: string, evt: HlsSubtitleTracksUpdated) => {
      data.subtitleTracks = evt?.subtitleTracks ?? data.subtitleTracks;
    },

    // Older way to handle bitrate changes.
    hlsManifestLoaded: (name: string, evt: HlsManifestLoadedEvent) => {
      log.trace(name, evt);
      data.levels = evt?.levels ? [...evt.levels] : [];
    },
    hlsLevelLoaded: (name: string, evt: HlsLevelLoadedEvent) => {
      const { level } = evt ?? {};
      const { levels, lastBitrate: bitrate } = data;
      log.trace(name, evt);
      if (level < data.levels.length && bitrate !== levels[level].bitrate) {
        data.lastBitrate = levels[level].bitrate;
        dispatchPlayerEvent({ e: bitratechanged, b: data.lastBitrate });
      }
    }
  };
  return {
    audioChangeHandler,
    bitrateChangeHandler,
    captionChangeHandler,
    data,
    handlers: handlers as Record<string, HLSEventCallback>
  };
}
export default generateHlsJsEventHandlers;
